import { BCDataGrid, BCNotification, findMessage } from '@bimser/components';
import IconFilterClear from '@bimser/icons/16/filter-clear';
import React from 'react';
import { NavigationBox } from './components/NavigationBox';
import IDMGridRemoteProps from './entities/IDMGridRemoteProps';
import useDMColumns from './hooks/useDMColumns';
import useDMCustomStore from './hooks/useDMCustomStore';
import { useDMGridEventListener } from './hooks/useDMGridEventListener';
import IconRefresh from '@bimser/icons/16/refresh';

const DMExplorerGrid = (props: IDMGridRemoteProps) => {
	const bcDataGridRef = React.useRef<BCDataGrid.Entities.BCDataGridRef>();
	useDMGridEventListener(BCDataGrid.Entities.DataGrids.DMExplorer, bcDataGridRef);

	const selectionRef = React.useRef<{
		checkBoxUpdating: boolean;
		selectAllCheckBox;
	}>({
		checkBoxUpdating: false,
		selectAllCheckBox: null
	});

	const columns: Array<BCDataGrid.Entities.IRemoteColumnProps> = useDMColumns(
		props.sceneId,
		props.gridContextItems
	);
	const dmCustomStore = useDMCustomStore(props, columns);

	const filterClearButtonOptions = React.useMemo(
		() => ({
			icon: IconFilterClear.info.getElement(),
			text: findMessage.get('100918'),
			hint: findMessage.get('100918'),
			onClick: () => {
				bcDataGridRef.current.instance().clearFilter();
				bcDataGridRef.current.instance().clearSorting();
			}
		}),
		[]
	);

	const goBackOptions = React.useMemo(
		() => ({
			text: findMessage.get('102994'),
			hint: findMessage.get('102994'),
			onClick: () => {
				props.navigationBox.goBack();
			},
			disabled:
				props.navigationBox?.items.findIndex(i => i.id === props.navigationBox?.value) === 0
		}),
		[props.navigationBox]
	);

	const goForwardOptions = React.useMemo(
		() => ({
			text: findMessage.get('102993'),
			hint: findMessage.get('102993'),
			onClick: () => {
				props.navigationBox.goForward();
			},
			disabled:
				props.navigationBox?.items.findIndex(i => i.id === props.navigationBox?.value) ===
				props.navigationBox?.items.length - 1
		}),
		[props.navigationBox]
	);

	const refreshButtonOptions = React.useMemo(
		() => ({
			icon: IconRefresh.info.getElement(),
			text: findMessage.get('102660'),
			hint: findMessage.get('102660'),
			onClick: () => {
				bcDataGridRef.current.instance().refresh();
			}
		}),
		[]
	);

	const selectionChanged = React.useCallback(values => {
		const selectionLimit = props.selectionOptions?.selectionLimit;
		if (selectionLimit && selectionLimit > 1) {
			if (values.length > selectionLimit) {
				BCNotification.warning({ message: findMessage.get('101399') });
				setTimeout(() => {
					bcDataGridRef.current.instance().deselectRows([values[selectionLimit]]);
				}, 10);
				return;
			}
		}

		const selectedRows = bcDataGridRef.current?.instance().getSelectedRowsData();
		props.onSelectionChange(selectedRows);
	}, []);

	const isSelectable = item => {
		if (props.selectionOptions?.restrict && item.TYPE !== 2) {
			return false;
		}

		return true;
	};

	const isSelectAll = dataGrid => {
		let items = [];
		dataGrid
			.getDataSource()
			.store()
			.load()
			.then(data => {
				items = data;
			});
		let selectableItems = items.filter(isSelectable);
		let selectedRowKeys = dataGrid.option('selectedRowKeys');
		if (!selectedRowKeys?.length) {
			return false;
		}
		return selectedRowKeys.length >= selectableItems.length ? true : undefined;
	};

	const onEditorPreparing = React.useCallback(e => {
		let dataGrid = e.component;
		if (e.type !== 'selection') return;
		if (e.parentType === 'dataRow' && e.row && !isSelectable(e.row.data))
			e.editorOptions.disabled = true;
		if (e.parentType === 'headerRow') {
			e.editorOptions.onInitialized = e => {
				if (e.component) selectionRef.current.selectAllCheckBox = e.component;
			};
			e.editorOptions.value = isSelectAll(dataGrid);
			e.editorOptions.onValueChanged = e => {
				if (!e.event) {
					if (e.previousValue && selectionRef.current.checkBoxUpdating)
						e.component.option('value', e.previousValue);
					return;
				}
				if (isSelectAll(dataGrid) === e.value) return;
				e.value ? dataGrid.selectAll() : dataGrid.deselectAll();
				e.event.preventDefault();
			};
		}
	}, []);

	const onSelectionChanged = React.useCallback(e => {
		const deselectRowKeys: number[] = [];
		e.selectedRowsData.forEach(item => {
			if (!isSelectable(item)) deselectRowKeys.push(e.component.keyOf(item));
		});
		if (deselectRowKeys.length) {
			e.component.deselectRows(deselectRowKeys);
		}

		selectionRef.current.checkBoxUpdating = true;
		const selectAllCheckBox = selectionRef.current.selectAllCheckBox;
		selectAllCheckBox?.option('value', isSelectAll(e.component));
		selectionRef.current.checkBoxUpdating = false;
	}, []);

	const getSelectionMode = () => {
		if (props.selectionOptions) {
			return props.selectionOptions?.allowMultipleSelection ? 'multiple' : 'single';
		} else {
			return 'multiple';
		}
	};

	return (
		<BCDataGrid.Components.DataGrid
			key={props.sceneId}
			ref={bcDataGridRef}
			name={BCDataGrid.Entities.DataGrids.DMExplorer}
			dataSource={dmCustomStore}
			columnResizingMode="widget"
			columnMinWidth={110}
			allowColumnResizing
			allowColumnReordering
			onRowDblClick={props.onDblClick}
			onSelectedRowKeysChange={selectionChanged}
			onContextMenuPreparing={props.onContextMenu}
			height="100%"
			width="100%"
			remoteOperations
			hoverStateEnabled
			showRowLines
			showColumnLines={false}
			onEditorPreparing={onEditorPreparing}
			onSelectionChanged={onSelectionChanged}
			onContentReady={props.onContentReady}
		>
			{columns.map((column: BCDataGrid.Entities.IColumnProps) => (
				<BCDataGrid.Components.Column
					key={column.name}
					{...column}
				/>
			))}
			<BCDataGrid.Components.Selection
				allowSelectAll={!props.selectionOptions?.enable}
				showCheckBoxesMode={'always'}
				mode={getSelectionMode()}
				selectAllMode="page"
			/>
			<BCDataGrid.Components.Pager
				visible
				displayMode="compact"
				allowedPageSizes={BCDataGrid.Constants.allowedPageSizes}
				showPageSizeSelector
			/>
			<BCDataGrid.Components.Paging defaultPageSize={10} />
			<BCDataGrid.Components.FilterRow visible />
			<BCDataGrid.Components.Sorting />
			<BCDataGrid.Components.Toolbar>
				<BCDataGrid.Components.Item
					location="before"
					widget="dxButton"
					locateInMenu="auto"
					options={goBackOptions}
					visible={!!props.navigationBox?.visible}
				/>
				<BCDataGrid.Components.Item
					location="before"
					locateInMenu="never"
					visible={!!props.navigationBox?.visible}
				>
					<NavigationBox {...props.navigationBox} />
				</BCDataGrid.Components.Item>
				<BCDataGrid.Components.Item
					location="before"
					widget="dxButton"
					locateInMenu="auto"
					options={goForwardOptions}
					visible={!!props.navigationBox?.visible}
				/>
				<BCDataGrid.Components.Item
					widget="dxButton"
					locateInMenu="auto"
					showText="inMenu"
					options={refreshButtonOptions}
					visible={!!props.showRefreshButton}
				/>
				<BCDataGrid.Components.Item
					widget="dxButton"
					locateInMenu="auto"
					showText="inMenu"
					options={filterClearButtonOptions}
				/>
				<BCDataGrid.Components.Item name="columnChooserButton" />
			</BCDataGrid.Components.Toolbar>
			<BCDataGrid.Components.ColumnChooser
				enabled
				mode="select"
			/>
			<BCDataGrid.Components.StateStoring
				enabled={props.stateStoringEnabled}
				storageKey={`DMGridsV3_${props.sceneId}`}
				type="localStorage"
				savingTimeout={0}
			/>
		</BCDataGrid.Components.DataGrid>
	);
};

export default DMExplorerGrid;
